import { useState, useEffect } from "react";
import { Spinner, Button, Badge } from "react-bootstrap";
import { Link } from "react-router-dom";
import user from "../../../../images/no_user_placeholder.png";
import { useGetTutorsQuery } from "../../../../store/services/student/TutorService";
import FiltersModal from "./FiltersModal";
import AverageStarRating from "../../../components/Dashboard/AverageStarRating";
import { trackMetaEvent } from "../../../utils/metaPixel";
import { pushDataLayerEvent } from "../../../utils/gtm";

export const BrowseTutors = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredTutors, setFilteredTutors] = useState([]);
  const [pageNumber, setPageNumber] = useState(1);
  const [allTutors, setAllTutors] = useState([]);
  const [tutors, setTutors] = useState([]);
  const [hasMore, setHasMore] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [queryParams, setQueryParams] = useState({ 
    pageNumber: 1, 
    take: 8, 
    search: "",
    language: "" // Add language param
  });

  // Filters state - now storing language ID instead of name
  const [filters, setFilters] = useState({
    language: "", // This will now store language ID (e.g., "1", "2") or "all"
    languageName: "", // Store language name for display
    times: [],
    days: [],
  });

  const { data, isLoading, isError, isFetching } = useGetTutorsQuery(queryParams);
  console.log("API Response:", data);
  console.log("Current filters:", filters);

  useEffect(() => {
    setTutors(data?.data?.tutorList || []);
  }, [data]);

  useEffect(() => {
    const tutorsCount = data?.data?.tutorCount || 0;
  
    if (tutors.length > 0) {
      if (pageNumber === 1) {
        setAllTutors(tutors);
      } else {
        setAllTutors((prev) => [...prev, ...tutors]);
      }
  
      if (tutors.length < 8) setHasMore(false);
      else if (tutors.length < tutorsCount) setHasMore(true);
      else setHasMore(false);
    } else if (pageNumber === 1) {
      setAllTutors([]);
      setHasMore(false);
    }
  }, [tutors]);

  useEffect(() => {
    setFilteredTutors(allTutors);
  }, [allTutors]);

  // Track Tutor List Loaded -> ViewContent after successful data load
  useEffect(() => {
    const metaEventId = data?.data?.metaEventId;
    const googleEventId = data?.data?.googleEventId;
    const tutorList = data?.data?.tutorList || [];

    if (metaEventId && tutorList.length > 0) {
      trackMetaEvent(
        "ViewContent",
        {
          content_name: "Tutor List",
          item_ids: tutorList.map((tutor) => tutor.id),
          content_type: "product",
        },
        metaEventId
      );
    }

    if (tutorList.length > 0) {
      pushDataLayerEvent("tutor_list_view", {
        google_event_id: googleEventId,
        tutor_count: tutorList.length,
        tutor_ids: tutorList.map((tutor) => tutor.id),
      });
    }
  }, [data]);

  // Check if any filters are applied
  const hasActiveFilters = (filters.language && filters.language !== "all") || 
                          filters.times.length > 0 || 
                          filters.days.length > 0;

  // Clear all filters
  const clearAllFilters = () => {
    setFilters({
      language: "",
      languageName: "",
      times: [],
      days: [],
    });
    setSearchTerm("");
    setPageNumber(1);
    setAllTutors([]); // Clear current results to fetch fresh ones
    setHasMore(true);
    // Reset to initial state without any filters
    setQueryParams({ 
      pageNumber: 1, 
      take: 8, 
      search: "",
      language: "" 
    });
  };

  useEffect(() => {
    const newParams = {
      pageNumber: 1,
      take: 8,
      search: searchTerm || "",
      language: filters.language && filters.language !== "all" ? filters.language : ""
    };
  
    if (filters.times.length > 0) newParams.times = filters.times;
    if (filters.days.length > 0) newParams.days = filters.days;
  
    setPageNumber(1);
    setAllTutors([]);
    setHasMore(true);
    setQueryParams(newParams);
  }, [searchTerm, filters]);

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleViewMore = () => {
    if (hasMore && !isFetching) {
        const nextPage = pageNumber + 1;
        setPageNumber(nextPage);
    
        setQueryParams((prev) => ({
          ...prev,
          pageNumber: nextPage,
        }));
    }
  };

  const applyFilters = () => {
    const newParams = {
      pageNumber: 1,
      take: 8,
      search: searchTerm || "",
      language: filters.language && filters.language !== "all" ? filters.language : ""
    };
  
    if (filters.times.length > 0) {
      newParams.times = filters.times;
    }
  
    if (filters.days.length > 0) {
      newParams.days = filters.days;
    }
  
    setPageNumber(1);
    setAllTutors([]);
    setHasMore(true);
    setQueryParams(newParams);
    setShowFilters(false);
  };

  // Prevent image right-click and drag
  const handleImageContextMenu = (e) => {
    e.preventDefault();
  };

  const handleImageDragStart = (e) => {
    e.preventDefault();
  };

  if (isLoading && pageNumber === 1) {
    return (
      <div className="text-center mt-5">
        <Spinner animation="border" /> Loading tutors...
      </div>
    );
  }

  if (isError) {
    return <div className="text-danger text-center mt-5">Failed to load tutors.</div>;
  }

  // Helper function to display active filter label
  const getActiveFilterLabel = () => {
    if (filters.language && filters.language !== "all") {
      return filters.languageName || "Language selected";
    }
    return "";
  };

  return (
    <div className="row">
      <div className="col-xl-12">
        <div className="page-title flex-wrap d-flex justify-content-between align-items-center mb-4">
          {/* Search */}
          <div className="input-group search-area" style={{ maxWidth: "400px" }}>
            <input
              type="text"
              className="form-control"
              placeholder="Search tutors..."
              value={searchTerm}
              onChange={handleSearchChange}
            />
            <span className="input-group-text">
              <svg width="15" height="15" viewBox="0 0 18 18" fill="none">
                <path
                  d="M17.5605 15.4395L13.7527 11.6317C14.5395 10.446 15 9.02625 15 7.5C15 3.3645 11.6355 0 7.5 0C3.3645 0 0 3.3645 0 7.5C0 11.6355 3.3645 15 7.5 15C9.02625 15 10.446 14.5395 11.6317 13.7527L15.4395 17.5605C16.0245 18.1462 16.9755 18.1462 17.5605 17.5605C18.1462 16.9747 18.1462 16.0252 17.5605 15.4395ZM2.25 7.5C2.25 4.605 4.605 2.25 7.5 2.25C10.395 2.25 12.75 4.605 12.75 7.5C12.75 10.395 10.395 12.75 7.5 12.75C4.605 12.75 2.25 10.395 2.25 7.5V7.5Z"
                  fill="#01A3FF"
                />
              </svg>
            </span>
          </div>

          {/* Filter Buttons */}
          <div className="d-flex gap-2 align-items-center">
            {hasActiveFilters && (
              <div className="d-flex align-items-center gap-2">
                {/* {getActiveFilterLabel() && (
                  <Badge bg="info" className="px-3 py-2">
                    <i className="fa fa-language me-1"></i>
                    {getActiveFilterLabel()}
                  </Badge>
                )} */}
                {/* {filters.times.length > 0 && (
                  <Badge bg="warning" text="dark" className="px-3 py-2">
                    <i className="fa fa-clock me-1"></i>
                    {filters.times.length} time slot{filters.times.length > 1 ? 's' : ''}
                  </Badge>
                )}
                {filters.days.length > 0 && (
                  <Badge bg="success" className="px-3 py-2">
                    <i className="fa fa-calendar me-1"></i>
                    {filters.days.length} day{filters.days.length > 1 ? 's' : ''}
                  </Badge>
                )} */}
                <Button variant="outline-danger" size="sm" onClick={clearAllFilters}>
                  <i className="fa fa-times me-1"></i>Clear All
                </Button>
              </div>
            )}
            <Button variant="outline-primary" onClick={() => setShowFilters(true)}>
              <i className="fa fa-filter me-2"></i>Filters
            </Button>
          </div>
        </div>
      </div>

      {/* Tutor Cards */}
      <div className="col-xl-12">
        <div className="row">
          {filteredTutors.length > 0 ? (
            filteredTutors.map((tutor) => {
              const hasRatings = tutor.averageRating > 0 && tutor.totalReviews > 0;
              
              return (
                <div className="col-xl-3 col-lg-4 col-sm-6 mb-4" key={tutor.id}>
                  <div className="card tutor-card h-100 shadow-sm border-0 hover-shadow">
                    <div className="card-body d-flex flex-column p-4">
                      {/* Profile Header */}
                      <div className="text-center mb-3">
                        <div className="position-relative d-inline-block">
                          <img
                            src={
                              tutor?.imagePath && tutor.imagePath.trim() !== ""
                                ? `${process.env.REACT_APP_IMAGE_PATH}${tutor.imagePath}`
                                : user
                            }
                            alt={`${tutor.name}'s profile`}
                            className="rounded-circle border"
                            style={{
                              width: '100px',
                              height: '100px',
                              objectFit: 'cover',
                              userSelect: 'none',
                              pointerEvents: 'none'
                            }}
                            onError={(e) => {
                              e.target.src = user;
                            }}
                            onContextMenu={handleImageContextMenu}
                            onDragStart={handleImageDragStart}
                          />
                          {/* Online Status Indicator */}
                          <div 
                            className="position-absolute bottom-0 end-0 rounded-circle bg-success border border-2 border-white"
                            style={{ width: '15px', height: '15px' }}
                            title="Available"
                          ></div>
                        </div>
                      </div>

                      {/* Tutor Name */}
                      <div className="text-center mb-3">
                        <h5 className="mb-2 fw-bold text-dark">{tutor.name}</h5>
                        
                        {/* Language with Flag */}
                        <div className="d-flex align-items-center justify-content-center mb-3">
                          {tutor.countryImage && (
                            <img 
                              src={`${process.env.REACT_APP_FLAG_IMAGE_PATH}${tutor.countryImage}`}
                              alt={tutor.expertise}
                              className="rounded me-2"
                              style={{
                                width: '24px',
                                height: '18px',
                                objectFit: 'cover'
                              }}
                            />
                          )}
                          <Badge bg="light" text="dark" className="fw-normal px-3 py-2">
                            {tutor.expertise}
                          </Badge>
                        </div>
                      </div>

                      {/* Tutor Details - Centered without labels */}
                      <div className="tutor-details text-center mb-4">
                        {/* Gender */}
                        <div className="mb-2">
                          <div className="d-flex align-items-center justify-content-center">
                            <i className="fa fa-venus-mars text-primary me-2"></i>
                            <span className="fw-medium text-dark">
                              {tutor.gender || 'Not specified'}
                            </span>
                          </div>
                        </div>
                        
                        {/* Age */}
                        <div className="mb-2">
                          <div className="d-flex align-items-center justify-content-center">
                            <i className="fa fa-user text-info me-2"></i>
                            <span className="fw-medium text-dark">
                              {tutor.age ? `${tutor.age} years old` : 'Age not specified'}
                            </span>
                          </div>
                        </div>

                        {/* Ratings */}
                        <div className="mb-2">
                          <div className="d-flex align-items-center justify-content-center">
                            <i className="fa fa-star text-warning me-2"></i>
                            {hasRatings ? (
                              <div className="d-flex align-items-center">
                                <AverageStarRating level={tutor.averageRating} />
                                <span className="fw-bold text-dark ms-2">
                                  {tutor.averageRating.toFixed(1)}
                                </span>
                                <small className="text-muted ms-2">
                                  ({tutor.totalReviews} review{tutor.totalReviews !== 1 ? 's' : ''})
                                </small>
                              </div>
                            ) : (
                              <span className="text-muted fst-italic">Not rated yet</span>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Action Button */}
                      <div className="mt-auto pt-3">
                        <Link
                          to={`/tutor-profile/${tutor.id}`}
                          className="btn btn-primary w-100 d-flex align-items-center justify-content-center py-2"
                        >
                          <i className="fa fa-eye me-2"></i>
                          View Profile
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })
          ) : (
            <div className="col-12 text-center py-5">
              {isFetching ? (
                <div className="d-flex flex-column align-items-center justify-content-center">
                  <Spinner animation="border" variant="primary" className="mb-3" />
                  <h5 className="text-muted">Searching for tutors...</h5>
                  <p className="text-muted">Please wait while we find the best matches for you.</p>
                </div>
              ) : (
                <div className="d-flex flex-column align-items-center justify-content-center">
                  <i className="fa fa-users text-muted mb-3" style={{ fontSize: '3rem' }}></i>
                  <h5 className="text-muted mb-2">No tutors found</h5>
                  <p className="text-muted mb-3">
                    {hasActiveFilters || searchTerm 
                      ? "Try adjusting your filters or search terms" 
                      : "No tutors are currently available"
                    }
                  </p>
                  {(hasActiveFilters || searchTerm) && (
                    <Button variant="outline-primary" onClick={clearAllFilters}>
                      Clear All Filters
                    </Button>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* View More Button */}
        {hasMore && filteredTutors.length > 0 && (
          <div className="text-center mt-4 mb-4">
            <Button 
              variant="outline-primary" 
              onClick={handleViewMore} 
              disabled={isFetching}
              size="lg"
              className="px-5"
            >
              {isFetching ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Loading More Tutors...
                </>
              ) : (
                <>
                  <i className="fa fa-arrow-down me-2"></i>
                  Load More Tutors
                </>
              )}
            </Button>
          </div>
        )}
      </div>

      {/* Filters Modal */}
      <FiltersModal
        show={showFilters}
        onHide={() => setShowFilters(false)}
        filters={filters}
        setFilters={setFilters}
        onApply={applyFilters}
        hasActiveFilters={hasActiveFilters}
        onClearFilters={clearAllFilters}
      />
    </div>
  );
};